import { useImageLoader } from '../../hooks/useImageLoader';
import type { ChibiImage } from '../../types';

interface ImageCardProps {
  image: ChibiImage;
  onClick: () => void;
  layout?: 'grid' | 'feed' | 'stack';
}

export function ImageCard({ image, onClick, layout = 'grid' }: ImageCardProps) {
  const { imgRef, imageSrc, isLoading } = useImageLoader(image.chibi);

  if (layout === 'grid') {
    return (
      <div
        onClick={onClick}
        class="bg-white rounded-lg shadow-md overflow-hidden transform transition-transform hover:scale-105 cursor-pointer"
      >
        <div class="aspect-square relative bg-gray-100">
          {isLoading && (
            <div class="absolute inset-0 flex items-center justify-center">
              <div class="w-8 h-8 border-2 border-soft-blue border-t-transparent rounded-full animate-spin" />
            </div>
          )}
          <img
            ref={imgRef}
            src={imageSrc}
            alt={image.title}
            class={`w-full h-full object-cover transition-opacity duration-300 ${isLoading ? 'opacity-0' : 'opacity-100'}`}
          />
        </div>
        <div class="p-3">
          <h3 class="font-semibold text-sm text-gray-800 truncate">
            {image.title}
          </h3>
          <div class="flex flex-wrap gap-1 mt-2">
            {image.tags.slice(0, 2).map(tag => (
              <span
                key={tag}
                class="px-2 py-1 text-xs bg-pink-100 text-gray-700 rounded-full"
              >
                {tag}
              </span>
            ))}
            {image.tags.length > 2 && (
              <span class="px-2 py-1 text-xs bg-gray-100 text-gray-600 rounded-full">
                +{image.tags.length - 2}
              </span>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (layout === 'feed') {
    return (
      <div class="bg-white rounded-lg shadow-md overflow-hidden">
        <div class="p-4 border-b">
          <h3 class="font-semibold text-lg">{image.title}</h3>
          <p class="text-sm text-gray-600 mt-1">{image.description}</p>
        </div>
        <div 
          onClick={onClick}
          class="relative cursor-pointer"
        >
          <div class="aspect-square relative bg-gray-100">
            {isLoading && (
              <div class="absolute inset-0 flex items-center justify-center">
                <div class="w-8 h-8 border-2 border-soft-blue border-t-transparent rounded-full animate-spin" />
              </div>
            )}
            <img
              ref={imgRef}
              src={imageSrc}
              alt={image.title}
              class={`w-full h-full object-cover transition-opacity duration-300 ${isLoading ? 'opacity-0' : 'opacity-100'}`}
            />
          </div>
          <div class="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent opacity-0 hover:opacity-100 transition-opacity flex items-end p-4">
            <p class="text-white text-sm">Tap to view full screen</p>
          </div>
        </div>
        <div class="p-4">
          <div class="flex flex-wrap gap-2">
            {image.tags.map(tag => (
              <span
                key={tag}
                class="px-3 py-1 text-sm bg-soft-blue/10 text-soft-blue rounded-full"
              >
                #{tag}
              </span>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Stack layout
  return (
    <div class="absolute inset-0 bg-white rounded-2xl shadow-xl overflow-hidden">
      <div class="h-full flex flex-col">
        <div class="flex-1 relative bg-gray-100">
          {isLoading && (
            <div class="absolute inset-0 flex items-center justify-center">
              <div class="w-12 h-12 border-3 border-soft-blue border-t-transparent rounded-full animate-spin" />
            </div>
          )}
          <img
            ref={imgRef}
            src={imageSrc}
            alt={image.title}
            onClick={onClick}
            class={`w-full h-full object-cover transition-opacity duration-300 ${isLoading ? 'opacity-0' : 'opacity-100'}`}
          />
        </div>
        <div class="p-6">
          <h2 class="text-2xl font-bold mb-2">{image.title}</h2>
          <p class="text-gray-600 mb-4">{image.description}</p>
          <div class="flex flex-wrap gap-2">
            {image.tags.map(tag => (
              <span
                key={tag}
                class="px-3 py-1 bg-pink-100 text-gray-700 rounded-full"
              >
                {tag}
              </span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}